import { Form, router } from "@inertiajs/react"
import {
  AlertCircle,
  Check,
  ChevronDown,
  Eye,
  Heart,
  MessageCircle,
  RefreshCw,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { StreakChip } from "@/components/creator/streak-chip"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { formatCompactNumber } from "@/lib/format-compact-number"
import { cn } from "@/lib/utils"
import {
  creatorCollabSubmissionsPath,
  eligibleCreatorCollabSubmissionsPath,
} from "@/routes"
import type { EligibleMediaTile, PickerProps } from "@/types/submissions"

import { PickerBrandStrip } from "./picker-brand-strip"
import { TikTokProfileStrip } from "./tiktok-profile-strip"

/**
 * OKS-35 / OKS-391 / OKS-481 / OKS-493 / OKS-526 — TikTok post picker (UX Spec §09).
 *
 * A TikTok-profile-grid surface inside a `Dialog` (near-fullscreen bottom
 * Sheet on mobile). Top to bottom: a brand strip (the mission's brand) →
 * the creator's TikTok identity strip → a head block → a 3-column grid of
 * vertical 9:16 thumbnails → a sticky submit bar that grows a metrics row
 * + a branded "Submit to {brand}" CTA once a tile is selected.
 *
 * OKS-481 reshape vs OKS-391:
 *   - adds the `PickerBrandStrip` + head copy + selected-metrics row;
 *   - replaces the inline caption-expand `PickerPreview` with the metrics
 *     row (the selection affordance is now the metrics + branded CTA);
 *   - "Used" posts (the existing brand-scoped dedupe — `already_submitted_*`)
 *     render a GENERIC "Used" pill, inert + non-selectable. The badge never
 *     carries another brand's mission title (isolation P0); cross-brand reuse
 *     stays allowed server-side (no global used-once — deferred to CTPO);
 *   - on submit the dialog stays mounted (`preserveState`) and flips to an
 *     in-dialog Submitted success state (StreakChip + push copy + Done),
 *     driven by an explicit FE `phase` — NOT a prop-merge of the prior
 *     picker tiles with the redirect response (OKS-465 lesson).
 *
 * Eligible-media payload comes from an Inertia partial reload:
 *   `router.get(eligibleCreatorCollabSubmissionsPath, { unfiltered })`
 * The controller still redirects on submit (Crew mutation rule); the
 * Submitted state is reached via the Form's `onSuccess`.
 */
interface Props {
  collabId: number
  picker?: PickerProps
  /** Mission title for the head subtitle. */
  missionTitle: string
  /**
   * OKS-388 — post-submit posting streak (page prop, threaded down). Read
   * ONLY in the Submitted state for the StreakChip; `null`/absent renders
   * no chip. After the submit redirect this carries the incremented run.
   */
  streak?: { current_days: number } | null
  open: boolean
  onOpenChange: (next: boolean) => void
  trigger?: React.ReactElement
}

type Phase = "browse" | "submitted"

export function TikTokPostPicker({
  collabId,
  picker,
  missionTitle,
  streak,
  open,
  onOpenChange,
  trigger,
}: Props) {
  const { t } = useTranslation()
  const [unfiltered, setUnfiltered] = useState<boolean>(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [phase, setPhase] = useState<Phase>("browse")
  const [loading, setLoading] = useState<boolean>(false)

  // Re-fetch when the dialog opens or the toggle flips. Loading + selection
  // + phase resets fire on `router.get`'s lifecycle callbacks so the effect
  // body stays free of immediate setState (`react-hooks/set-state-in-effect`).
  // Resetting `phase` to "browse" on (re)open guarantees a reopened dialog
  // never shows a stale Submitted state.
  useEffect(() => {
    if (!open) return
    router.get(
      eligibleCreatorCollabSubmissionsPath(collabId),
      { unfiltered: unfiltered ? "true" : "false" },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["picker"],
        onStart: () => {
          setLoading(true)
          setSelectedId(null)
          setPhase("browse")
        },
        onFinish: () => setLoading(false),
      },
    )
  }, [open, unfiltered, collabId])

  const matchesThisCollab = picker?.collab_id === collabId
  const tiles = matchesThisCollab ? (picker?.tiles ?? []) : []
  const errorCode = matchesThisCollab ? picker?.error_code : null
  const profile = matchesThisCollab ? (picker?.profile ?? null) : null
  const brand = matchesThisCollab ? (picker?.brand ?? null) : null
  const brandName = brand?.name ?? ""
  const selectedTile = tiles.find((tile) => tile.id === selectedId) ?? null

  // One-tap select / re-tap deselect (UX Spec §09 interaction). The metrics
  // row + branded CTA are the selection affordance (no inline preview).
  const handleSelect = (id: string) => {
    setSelectedId((prev) => (prev === id ? null : id))
  }

  // OKS-737 — manual Refresh: re-fire the SAME partial reload the effect uses,
  // adding `refresh: "true"` so the controller forces a live pull past the
  // OKS-497 video-list cache. Reuses the existing `loading` lifecycle (skeleton
  // + selection reset + phase "browse") as the single loading source of truth,
  // so the button spinner can never outlive the pull. Guard: return early while
  // a pull is in flight so rapid double-taps don't stack requests (the button
  // is also `disabled={loading}`). `unfiltered` is carried through, so a live
  // pull still respects the hashtag toggle.
  const handleRefresh = () => {
    if (loading) return
    router.get(
      eligibleCreatorCollabSubmissionsPath(collabId),
      { unfiltered: unfiltered ? "true" : "false", refresh: "true" },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["picker"],
        onStart: () => {
          setLoading(true)
          setSelectedId(null)
          setPhase("browse")
        },
        onFinish: () => setLoading(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger render={trigger} />}
      <DialogContent
        data-testid={`tiktok-post-picker-${collabId}`}
        className="sm:max-w-2xl"
      >
        {phase === "submitted" ? (
          <PickerSubmittedState
            collabId={collabId}
            brandName={brandName}
            streak={streak}
            onDone={() => onOpenChange(false)}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t("submissions.picker.head_title")}</DialogTitle>
              <DialogDescription>
                {t("submissions.picker.head_subtitle", {
                  mission: missionTitle,
                })}
              </DialogDescription>
            </DialogHeader>

            {/* OKS-737 — aria-busy announces the in-flight refresh to AT, so
                the loading state isn't conveyed by the spinner/skeleton alone. */}
            <div
              className="max-h-[60vh] space-y-3 overflow-y-auto"
              aria-busy={loading}
            >
              {loading ? (
                <ProfileStripSkeleton collabId={collabId} />
              ) : (
                <>
                  {brand && (
                    <PickerBrandStrip collabId={collabId} brand={brand} />
                  )}
                  {profile && (
                    <TikTokProfileStrip collabId={collabId} profile={profile} />
                  )}
                </>
              )}

              <PickerBody
                collabId={collabId}
                loading={loading}
                errorCode={errorCode ?? null}
                tiles={tiles}
                selectedId={selectedId}
                onSelect={handleSelect}
                unfiltered={unfiltered}
              />
            </div>

            <DialogFooter
              data-testid={`tiktok-picker-footer-${collabId}`}
              className="flex-col gap-3 pb-[env(safe-area-inset-bottom)]"
            >
              {/*
                OKS-737 — hashtag toggle (left) + manual Refresh (right) share
                one row via `justify-between`; both re-fetch the grid, so they
                read coherently as a pair. Refresh sits above the sticky Submit
                CTA, within thumb reach, and clear of the Dialog's built-in top-
                right close (X).
              */}
              <div className="flex w-full items-center justify-between gap-2">
                <label className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Switch
                    checked={unfiltered}
                    onCheckedChange={setUnfiltered}
                    data-testid={`tiktok-picker-unfiltered-toggle-${collabId}`}
                    aria-label={t("submissions.picker.hashtag_filter_toggle")}
                  />
                  {t("submissions.picker.hashtag_filter_toggle")}
                </label>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={loading}
                  aria-label={t("submissions.picker.refresh_aria")}
                  data-testid={`tiktok-picker-refresh-${collabId}`}
                  className="min-h-11 shrink-0"
                >
                  <RefreshCw
                    aria-hidden="true"
                    className={cn("size-4", loading && "animate-spin")}
                  />
                  {t("submissions.picker.refresh")}
                </Button>
              </div>

              {selectedTile && (
                <SelectedMetricsRow collabId={collabId} tile={selectedTile} />
              )}

              <Form
                action={creatorCollabSubmissionsPath(collabId)}
                method="post"
                options={{ preserveScroll: true, preserveState: true }}
                onSuccess={() => setPhase("submitted")}
                className="w-full"
              >
                {({ processing }) => (
                  <>
                    <input
                      type="hidden"
                      name="tiktok_video_id"
                      value={selectedId ?? ""}
                    />
                    <Button
                      type="submit"
                      variant="default"
                      className="w-full"
                      disabled={selectedId === null || processing}
                      data-testid={`tiktok-picker-submit-${collabId}`}
                    >
                      {selectedId === null
                        ? t("submissions.picker.select_to_submit")
                        : t("submissions.picker.submit_to_brand", {
                            brand: brandName,
                          })}
                    </Button>
                  </>
                )}
              </Form>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

interface PickerBodyProps {
  collabId: number
  loading: boolean
  errorCode: PickerProps["error_code"] | null
  tiles: EligibleMediaTile[]
  selectedId: string | null
  onSelect: (id: string) => void
  unfiltered: boolean
}

/**
 * OKS-526 — Keyboard roaming for the OKS-493 listbox.
 *
 * The grid is a single tab-stop via ROVING TABINDEX: exactly one selectable
 * tile carries `tabIndex=0` (the roving entry point), the rest `-1`. That
 * entry point is derived at render — never via an effect — as
 * `activeId (if still selectable) → selectedId (if selectable) → first
 * selectable`. Deriving it means the roving point auto re-seeds on reopen /
 * filter reset (tiles + selection change together) and survives re-renders
 * without disturbing selection / in-dialog confirm / submit / used-once gating
 * (remount-sensitive welded-affordance component — memory
 * crew_section_relocation_remounts_picker).
 *
 * Arrow model is LINEAR (role="listbox" is a 1-D contract), clamped at the
 * ends (no wrap), and skips `aria-disabled`/Used tiles so focus never traps on
 * one: ↓/→ next selectable, ↑/← previous selectable, Home/End first/last
 * selectable. Enter/Space are intentionally NOT handled here — the active tile
 * has real DOM focus, so its native `<button>` fires `onClick → onSelect` for
 * free (same toggle as a tap, no double-fire).
 */
function PickerBody({
  collabId,
  loading,
  errorCode,
  tiles,
  selectedId,
  onSelect,
  unfiltered,
}: PickerBodyProps) {
  const { t } = useTranslation()
  // Roving entry-point override the user drives with arrows/Home/End. `null`
  // (or a now-invalid id) falls back to the derived seed below.
  const [activeId, setActiveId] = useState<string | null>(null)
  // Live DOM handles for the tile buttons, so arrow keys can move real focus.
  const tileRefs = useRef(new Map<string, HTMLButtonElement>())

  // OKS-736 — partition the payload: selectable tiles stay in the main
  // `role="listbox"` grid; Used tiles (already-submitted, brand-scoped dedupe)
  // move into a collapsed Collapsible below. Both keep the original payload
  // order. This is derived at render (pure) — no prop change, no server change.
  const selectable = tiles.filter((tile) => tile.ineligible_reason === null)
  const used = tiles.filter((tile) => tile.ineligible_reason !== null)

  // Selectable = not Used (Used tiles are inert `disabled` options, never
  // keyboard-reachable). Order follows DOM/render order for linear roaming.
  // OKS-666 guard: the roving order is selectable-only, so Used tiles never
  // enter the tab order whether the Collapsible is open or closed.
  const selectableIds = selectable.map((tile) => tile.id)

  // Derived roving entry point (see component doc). Pure — no effect.
  const resolvedActiveId =
    activeId !== null && selectableIds.includes(activeId)
      ? activeId
      : selectedId !== null && selectableIds.includes(selectedId)
        ? selectedId
        : (selectableIds[0] ?? null)

  const moveActiveTo = (id: string) => {
    setActiveId(id)
    tileRefs.current.get(id)?.focus()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const navKeys = [
      "ArrowDown",
      "ArrowRight",
      "ArrowUp",
      "ArrowLeft",
      "Home",
      "End",
    ]
    // Enter/Space (and everything else) fall through to native button
    // activation — no double-fire.
    if (!navKeys.includes(event.key) || selectableIds.length === 0) return
    event.preventDefault()

    const currentIndex = resolvedActiveId
      ? selectableIds.indexOf(resolvedActiveId)
      : 0
    let nextIndex = currentIndex
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        nextIndex = Math.min(currentIndex + 1, selectableIds.length - 1)
        break
      case "ArrowUp":
      case "ArrowLeft":
        nextIndex = Math.max(currentIndex - 1, 0)
        break
      case "Home":
        nextIndex = 0
        break
      case "End":
        nextIndex = selectableIds.length - 1
        break
    }
    moveActiveTo(selectableIds[nextIndex])
  }

  if (loading) {
    return (
      <div
        className="grid grid-cols-3 gap-2"
        data-testid={`tiktok-picker-loading-${collabId}`}
      >
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="aspect-[9/16] w-full" />
        ))}
      </div>
    )
  }

  if (
    errorCode === "tiktok_auth_revoked" ||
    errorCode === "missing_social_account"
  ) {
    return (
      <Alert
        variant="destructive"
        data-testid={`tiktok-picker-auth-revoked-${collabId}`}
      >
        <AlertCircle className="size-4" />
        <AlertTitle>{t("submissions.picker.tiktok_unreachable")}</AlertTitle>
        <AlertDescription>{t("submissions.picker.reconnect")}</AlertDescription>
      </Alert>
    )
  }

  if (errorCode) {
    return (
      <Alert
        variant="destructive"
        data-testid={`tiktok-picker-error-${collabId}`}
      >
        <AlertCircle className="size-4" />
        <AlertTitle>{t("submissions.picker.tiktok_unreachable")}</AlertTitle>
        <AlertDescription>
          {t(`submissions.errors.${errorCode}`)}
        </AlertDescription>
      </Alert>
    )
  }

  if (tiles.length === 0) {
    return (
      <Alert data-testid={`tiktok-picker-empty-${collabId}`}>
        <AlertTitle>
          {unfiltered
            ? t("submissions.picker.no_recent_posts")
            : t("submissions.picker.no_match")}
        </AlertTitle>
        <AlertDescription>
          {unfiltered
            ? t("submissions.picker.no_recent_posts_helper")
            : t("submissions.picker.no_match_toggle_hint")}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-3">
      {selectable.length > 0 ? (
        <div
          role="listbox"
          aria-label={t("submissions.picker.grid_aria")}
          className="grid grid-cols-3 gap-2"
          data-testid={`tiktok-picker-grid-${collabId}`}
          onKeyDown={handleKeyDown}
        >
          {selectable.map((tile) => (
            <PickerTile
              key={tile.id}
              tile={tile}
              isSelected={tile.id === selectedId}
              isActive={tile.id === resolvedActiveId}
              onSelect={() => onSelect(tile.id)}
              registerRef={(node) => {
                if (node) tileRefs.current.set(tile.id, node)
                else tileRefs.current.delete(tile.id)
              }}
            />
          ))}
        </div>
      ) : (
        // OKS-736 — all posts already used: a NON-destructive "nothing new"
        // Alert replaces the grid. Guard is `selectable.length === 0`, NOT
        // `tiles.length === 0` (that genuine-empty case short-circuits above
        // with no_match/no_recent), so this never shows the misleading
        // hashtag-filter copy when Used tiles still exist below.
        <Alert data-testid={`tiktok-picker-nothing-new-${collabId}`}>
          <AlertTitle>{t("submissions.picker.nothing_new_title")}</AlertTitle>
          <AlertDescription>
            {t("submissions.picker.nothing_new_helper")}
          </AlertDescription>
        </Alert>
      )}

      {used.length > 0 && (
        // OKS-736 — Used posts folded into a collapsed Collapsible. Rendered
        // only when ≥1 Used tile exists. The inner grid is `role="list"` (NOT
        // listbox) — Used tiles are inert, never options. Each Used tile keeps
        // the PickerTile `disabled` path (tabIndex=-1, aria-disabled), is never
        // the roving entry point (isActive=false), and is NOT registered into
        // `tileRefs` (no-op registerRef) — so the OKS-666 tab-order guard holds
        // open or closed. Brand isolation: generic "Used" pill only, no title.
        <Collapsible>
          <CollapsibleTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="group min-h-11 w-full justify-start"
                data-testid={`tiktok-picker-used-toggle-${collabId}`}
              />
            }
          >
            {t("submissions.picker.view_already_posted", {
              count: used.length,
            })}
            <ChevronDown
              aria-hidden="true"
              className="size-4 transition-transform group-data-[panel-open]:rotate-180"
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div
              role="list"
              className="grid grid-cols-3 gap-2 pt-2"
              data-testid={`tiktok-picker-used-grid-${collabId}`}
            >
              {used.map((tile) => (
                <PickerTile
                  key={tile.id}
                  tile={tile}
                  presentational
                  isSelected={false}
                  isActive={false}
                  onSelect={() => undefined}
                  registerRef={() => undefined}
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  )
}

/**
 * OKS-391 — Loading skeleton for the profile strip: an avatar circle + two
 * text-line skeletons, matching the populated strip's footprint so the
 * layout doesn't jump on load.
 */
function ProfileStripSkeleton({ collabId }: { collabId: number }) {
  return (
    <div
      data-testid={`tiktok-picker-profile-skeleton-${collabId}`}
      className="flex items-center gap-3 rounded-lg border p-3"
    >
      <Skeleton className="size-12 shrink-0 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-full" />
      </div>
    </div>
  )
}

interface PickerTileProps {
  tile: EligibleMediaTile
  isSelected: boolean
  /**
   * OKS-526 — this tile is the listbox's roving entry point: `tabIndex=0` +
   * `data-active="true"`. Exactly one tile per grid is active at a time, always
   * a selectable (non-Used) tile. The two attributes are driven off this single
   * boolean so they can never diverge.
   */
  isActive: boolean
  onSelect: () => void
  /** OKS-526 — registers the tile's <button> node for arrow-key focus moves. */
  registerRef: (node: HTMLButtonElement | null) => void
  /**
   * OKS-736 — presentational (inert) render for the folded "already posted"
   * grid. When true the tile is a `role="listitem"` (its container is a
   * `role="list"`, NOT a listbox) and drops the `option`/`aria-selected`
   * selection semantics — these tiles are never options. It stays `disabled`
   * + out of the roving order regardless (Used tiles are always `disabled`).
   * Defaults to false → the selectable listbox `option` render is unchanged.
   */
  presentational?: boolean
}

/**
 * OKS-481 — Grid tile. View-count overlay (kept from V1); accent ring +
 * check badge when selected; a GENERIC "Used" pill when the post is already
 * spent on a mission (brand-scoped dedupe). The Used badge carries NO mission
 * title — color is never the sole signal and no cross-brand data leaks.
 *
 * OKS-526 — participates in roving tabindex: the active tile is the single
 * tab-stop (`tabIndex=0`); the native button focus ring is the active-option
 * indicator. Used tiles stay natively `disabled` → unfocusable and skipped.
 *
 * OKS-736 — in `presentational` mode (the folded "already posted" grid) the
 * tile drops `option`/`aria-selected` semantics and is a `role="listitem"`;
 * it remains `disabled` + `tabIndex=-1` + `data-active="false"`.
 */
function PickerTile({
  tile,
  isSelected,
  isActive,
  onSelect,
  registerRef,
  presentational = false,
}: PickerTileProps) {
  const { t } = useTranslation()
  const used = tile.ineligible_reason !== null

  return (
    <button
      ref={registerRef}
      type="button"
      role={presentational ? "listitem" : "option"}
      aria-selected={presentational ? undefined : isSelected}
      aria-disabled={used}
      tabIndex={isActive ? 0 : -1}
      data-active={isActive}
      aria-label={t("submissions.picker.tile_aria", {
        when: tile.posted_at ?? "",
      })}
      disabled={used}
      onClick={onSelect}
      data-testid={`tiktok-picker-tile-${tile.id}`}
      className={cn(
        "group relative aspect-[9/16] overflow-hidden rounded-md border-2 transition",
        isSelected && "border-primary ring-primary/30 ring-2",
        !isSelected &&
          !used &&
          "hover:border-muted-foreground border-transparent",
        used && "cursor-not-allowed opacity-50",
      )}
    >
      {tile.thumbnail_url ? (
        <img
          src={tile.thumbnail_url}
          alt=""
          className="size-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="bg-muted size-full" aria-hidden="true" />
      )}

      {isSelected && (
        <span
          aria-hidden="true"
          className="bg-primary text-primary-foreground absolute top-1 right-1 flex size-5 items-center justify-center rounded-full"
        >
          <Check className="size-3" />
        </span>
      )}

      {tile.view_count !== null && (
        <span className="absolute right-1 bottom-1 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">
          <Eye aria-hidden="true" className="mr-0.5 inline size-3" />
          {formatCompactNumber(tile.view_count)}
        </span>
      )}

      {used && (
        <span
          data-testid={`tiktok-picker-used-${tile.id}`}
          className="bg-background/90 text-foreground absolute top-1 left-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
        >
          {t("submissions.picker.used_badge")}
        </span>
      )}
    </button>
  )
}

interface SelectedMetricsRowProps {
  collabId: number
  tile: EligibleMediaTile
}

/**
 * OKS-481 (§09) — Compact metrics row above the Submit CTA when a tile is
 * selected: `Your pick: 👁 {views} · ♥ {likes} · 💬 {comments}`. Each metric
 * hides gracefully when its value is null (a sparse TikTok payload). Numbers
 * are compacted ("24.8K") for the narrow 402px frame.
 */
function SelectedMetricsRow({ collabId, tile }: SelectedMetricsRowProps) {
  const { t } = useTranslation()

  const metrics: { key: string; icon: typeof Eye; value: number }[] = []
  if (tile.view_count !== null)
    metrics.push({ key: "views", icon: Eye, value: tile.view_count })
  if (tile.like_count !== null)
    metrics.push({ key: "likes", icon: Heart, value: tile.like_count })
  if (tile.comment_count !== null)
    metrics.push({
      key: "comments",
      icon: MessageCircle,
      value: tile.comment_count,
    })

  return (
    <div
      data-testid={`tiktok-picker-metrics-${collabId}`}
      className="text-muted-foreground flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-sm"
    >
      <span className="font-medium">{t("submissions.picker.your_pick")}</span>
      {metrics.map(({ key, icon: Icon, value }) => (
        <span key={key} className="inline-flex items-center gap-1">
          <Icon aria-hidden="true" className="size-3.5" />
          {formatCompactNumber(value)}
        </span>
      ))}
    </div>
  )
}

interface PickerSubmittedStateProps {
  collabId: number
  brandName: string
  streak?: { current_days: number } | null
  onDone: () => void
}

/**
 * OKS-481 (§09) — In-dialog Submitted success state. Replaces the picker body
 * after a successful submit: a centered check, the posting StreakChip (the
 * post-submit run), "Submitted to {brand}", the review/push helper, and a Done
 * button that closes the dialog (the panel already shows the new row + streak
 * from the redirect's reloaded props).
 *
 * Driven by the picker's explicit `phase` state — never by a prop merge of the
 * old picker tiles with the redirect response (OKS-465). The DialogTitle is
 * kept for the Radix a11y contract.
 */
function PickerSubmittedState({
  collabId,
  brandName,
  streak,
  onDone,
}: PickerSubmittedStateProps) {
  const { t } = useTranslation()

  return (
    <div
      data-testid={`tiktok-picker-submitted-${collabId}`}
      className="flex flex-col items-center gap-4 py-6 text-center"
    >
      <DialogHeader className="items-center">
        <span
          aria-hidden="true"
          className="bg-primary text-primary-foreground flex size-12 items-center justify-center rounded-full"
        >
          <Check className="size-6" />
        </span>
        <DialogTitle>{t("submissions.picker.submitted_title")}</DialogTitle>
        <DialogDescription>
          {t("submissions.picker.submitted_to_brand", { brand: brandName })}
        </DialogDescription>
      </DialogHeader>

      {streak && streak.current_days >= 1 && (
        <StreakChip currentDays={streak.current_days} />
      )}

      <p className="text-muted-foreground max-w-xs text-sm">
        {t("submissions.picker.submitted_push_helper", { brand: brandName })}
      </p>

      <Button
        type="button"
        variant="default"
        className="w-full"
        onClick={onDone}
        data-testid={`tiktok-picker-done-${collabId}`}
      >
        {t("submissions.picker.done_cta")}
      </Button>
    </div>
  )
}
